<template>
  <v-container fluid pt-0>
    <v-row align="center" justify="end">
      <v-col cols="3">
        <dates-range-picker
          dense
          :gt-date-from-string="dateFromFilter"
          :gt-date-to-string="dateToFilter"
          :outlined="outlinedInputs"
          @onRangeSelected="onDatesChange"
        ></dates-range-picker>
      </v-col>
      <v-col>
        <gt-multi-select
          :items="services"
          label="Service Filter"
          item-text="node.name"
          item-value="node.id"
          v-model="serviceFilter"
          :outlined="outlinedInputs"
          dense
        ></gt-multi-select>
      </v-col>
      <v-col>
        <v-select
          :items="daysOfWeek"
          item-text="name"
          item-value="value"
          label="Day of Week Filter"
          v-model="dayOfWeekFilter"
          multiple
          :outlined="outlinedInputs"
          hide-details
          dense
        />
      </v-col>
      <v-col>
        <v-text-field
          label="Name Filter"
          v-model="nameInputFilter"
          @blur="onNameFilterBlur"
          clearable
          dense
          :outlined="outlinedInputs"
          hide-details
        />
      </v-col>
      <v-col class="py-0">
        <v-select
          v-if="showGroupedView"
          :items="groupByOptions"
          v-model="selectedGroupedBy"
          label="Group by"
          item-text="name"
          return-object
          :outlined="outlinedInputs"
          hide-details
          dense
          @change="onSelectedGroupedByChange"
        ></v-select>
      </v-col>
      <v-switch
        prepend-icon="mdi-view-list"
        class="mr-3"
        label="Group View"
        v-model="showGroupedView"
        @change="onShowGroupedViewChange"
      ></v-switch>
      <div class="pr-2">
        <list-visible-columns-and-filter
          :visible-columns="visibleColumns"
          :selectable-headers="selectableHeaders"
          v-model="search"
          @change="onVisibleColumnsChange"
        ></list-visible-columns-and-filter>
      </div>
      <add-allocations-dialog
        :services="services"
        @create="onAllocationsCreate"
      >
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            color="primary"
            :disabled="$store.getters.hasAccessTo('suppliers:view_only')"
          >
            <v-icon left>mdi-plus-circle</v-icon>Add Allocations
          </v-btn>
        </template>
      </add-allocations-dialog>
    </v-row>
    <v-row v-if="showGroupedView">
      <v-col class="pa-0">
        <list-grouped :group="selectedGroupedBy" :items="allocations" panels>
          <template v-slot:table="{ items }">
            <v-card outlined>
              <list-grouped
                :group="byAllocationAndDate"
                :items="items"
                panels
                v-if="
                  selectedGroupedBy &&
                  selectedGroupedBy.name == 'Allocation Name'
                "
              >
                <template v-slot:table="{ items }">
                  <allocations-table
                    :items="items"
                    :headers="filteredHeaders"
                    :search="search"
                    @delete="onAllocationDelete"
                    @edit="onAllocationEdit"
                    @edit-mutlti="onEditMulti"
                    @deleteRecords="onDeleteRecords"
                    :loading="$apollo.loading"
                  ></allocations-table>
                </template>
              </list-grouped>
              <allocations-table
                v-else
                :items="items"
                :headers="filteredHeaders"
                :search="search"
                @delete="onAllocationDelete"
                @edit="onAllocationEdit"
                @deleteRecords="onDeleteRecords"
                :loading="$apollo.loading"
                @edit-mutlti="onEditMulti"
              ></allocations-table>
            </v-card>
          </template>
        </list-grouped>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col class="pa-0">
        <v-card outlined>
          <allocations-table
            :items="allocations"
            :headers="filteredHeaders"
            :search="search"
            @delete="onAllocationDelete"
            @edit="onAllocationEdit"
            @edit-mutlti="onEditMulti"
            @deleteRecords="onDeleteRecords"
          ></allocations-table>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="debug">
      <pre>{{}}</pre>
    </v-row>
    <allocation-item-editor
      v-model="editedItem"
      :show="showAllocationItemEditor"
      @input="onAllocationItemSave"
      @close="showAllocationItemEditor = false"
    />
    <edit-allocations-dialog
      :show="showEditAllocationsDialog"
      :allocations="selected"
      @save="onSaveAllocations"
      @close="showEditAllocationsDialog = false"
    />
  </v-container>
</template>

<script>
import {
  getTotal,
  removeGQLTypename,
  sortByDateField,
} from "../../../../helpers/index";
import ListVisibleColumnsAndFilter from "../../shared/ListVisibleColumnsAndFilter";
import ListGrouped from "../../shared/ListGrouped";
import DatesRangePicker from "../../shared/DatesRangePicker";
import AllocationsTable from "./AllocationsTable";
import AddAllocationsDialog from "./AddAllocationsDialog";
import { ListMixin } from "../../../../mixins/ListMixin";
import AllocationItemEditor from "./AllocationItemEditor";
import { REMOVE_RECORDS } from "../../../../constants/graphql";
import GtMultiSelect from "../../shared/GtMultiSelect.vue";
import gql from "graphql-tag";
import EditAllocationsDialog from "./EditAllocationsDialog.vue";
const moment = require("moment");
const QUERY = gql`
  query query($supplierId: ID!, $filter: SupplierAllocationFilterType) {
    supplier(id: $supplierId) {
      id
      allocations(filter: $filter) {
        edges {
          node {
            id
            name
            date
            type
            releaseDate
            uid
            releaseDays
            dayOfWeek
            groupId
            qty
            max1
            max2
            max3
            max4
            max5
            max6
            max7
            maxTotal
            priority
            taken
            agentGroup {
              name
            }
            service {
              id
              name
              notInUse
            }
          }
        }
      }
    }
  }
`;
export const ADD_ALLOCATION_MUTATION = gql`
  mutation addAllocation(
    $supplierId: ID!
    $supplierAddAllocationsInput: SupplierAddAllocationsInput
  ) {
    supplierAddAllocations(
      supplierId: $supplierId
      supplierAddAllocationsInput: $supplierAddAllocationsInput
    ) {
      success
      error
    }
  }
`;
export const DELETE_ALLOCATION_MUTATION = gql`
  mutation deleteAllocation($id: ID!) {
    deleteAllocation(id: $id) {
      id
    }
  }
`;
export const UPDATE_ALLOCATION_MUTATION = gql`
  mutation updateAllocation($id: ID!, $allocationInput: AllocationInput) {
    updateAllocation(id: $id, allocationInput: $allocationInput) {
      id
    }
  }
`;
export const UPDATE_ALLOCATIONS_MUTATION = gql`
  mutation updateAllocations($ids: [ID!]!, $input: AllocationInput) {
    updateAllocations(ids: $ids, input: $input) {
      success
      message
    }
  }
`;
export default {
  name: "supplier-allocations",
  props: {
    showTitle: {
      type: Boolean,
      default: () => true,
    },
    supplierId: {
      type: String,
      default: "",
    },
    services: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    ListVisibleColumnsAndFilter,
    ListGrouped,
    AllocationsTable,
    AddAllocationsDialog,
    AllocationItemEditor,
    DatesRangePicker,
    GtMultiSelect,
    EditAllocationsDialog,
  },
  mixins: [ListMixin],
  data() {
    const groupByOptions = [
      {
        name: "Service",
        getKey: (item) => item.service.name,
        getLabel: (item) => item.service.name,
      },
      {
        name: "Allocation Name",
        getKey: (item) => item.name,
        getLabel: (item) => item.name,
      },
      {
        name: "Agent/Group",
        getKey: (item) => item.groupId,
        getLabel: (item) => item.agentGroupName,
      },
      {
        name: "Date Range",
        getKey: (item) => item.uid,
        getLabel: (item) => {
          const rangeItems = this.allocations.filter((i) => i.uid == item.uid);
          return `${
            rangeItems[0].name
          } - from: ${this.$options.filters.formatDate(
            rangeItems[rangeItems.length - 1].date
          )}, to:  ${this.$options.filters.formatDate(rangeItems[0].date)}`;
        },
      },
      {
        name: "Allocation Name and Date",
        getKey: (item) => `${item.name}-${item.date}`,
        getLabel: (item) =>
          `${item.name}: ${this.$options.filters.formatDate(
            item.date
          )}, Total Allocations: ${item.qty}, Remaining: ${item.remaining}`,
      },
    ];
    return {
      debug: false,
      showGroupedView: this.$store.getters.localSettings(
        `${this.$options.name}-showGroupedView`,
        false
      ),
      search: "",
      visibleColumns: this.$store.getters.localSettings(this.$options.name, [
        "name",
        "date",
        "agentGroupName",
        "service",
        "qty",
        "used",
        "isShared",
      ]),
      byAllocationAndDate: {
        name: "Allocation Name and Date",
        getKey: (item) => `${item.name}-${item.date}`,
        getLabel: (item) =>
          `${item.name}: ${this.$options.filters.formatDate(
            item.date
          )}, Total Allocations: ${item.qty}, Remaining: ${item.remaining}`,
      },
      headers: [
        {
          text: "Date",
          sortable: true,
          value: "date",
          name: "date",
        },
        {
          text: "Allocation Name",
          sortable: true,
          value: "name",
          name: "name",
        },
        {
          text: "Agent/Group",
          sortable: true,
          value: "agentGroupName",
          name: "agentGroupName",
        },
        {
          text: "Service",
          sortable: true,
          value: "service.name",
          name: "service",
        },
        {
          text: "Service Not In Use",
          sortable: false,
          value: "service.notInUse",
          name: "service_not_in_use",
        },
        {
          text: "Max Total",
          sortable: true,
          value: "maxTotal",
          name: "maxTotal",
        },
        {
          text: "Total",
          sortable: true,
          value: "qty",
          name: "qty",
        },
        {
          text: "Used",
          sortable: true,
          value: "taken",
          name: "used",
        },
        {
          text: "Type",
          sortable: true,
          value: "type",
          name: "type",
        },
        {
          text: "Remaining",
          sortable: true,
          value: "remaining",
          name: "remaining",
        },
        {
          text: "Release",
          sortable: true,
          value: "releaseDays",
          name: "release",
        },
        {
          text: "Release Date",
          sortable: true,
          value: "releaseDate",
          name: "releaseDate",
        },
        {
          text: "Shared",
          sortable: true,
          value: "isShared",
          name: "isShared",
        },
        {
          text: "Shared Services",
          sortable: false,
          value: "sharedServices",
          name: "sharedServices",
        },
        {
          text: "Actions",
          sortable: false,
          value: "actions",
          name: "actions",
          align: "center",
        },
      ],

      // selectedGroupedBy: JSON.parse(
      //   this.$store.getters.localSettings(
      //     `${this.$options.name}-selectedGroupedBy`,
      //     JSON.stringify({
      //       name: "Service",
      //       getKey: (item) => item.service.name,
      //       getLabel: (item) => item.service.name,
      //     })
      //   )
      // ),
      groupByOptions,

      selectedGroupedBy: groupByOptions.find(
        (item) =>
          item.name ==
          this.$store.getters.localSettings(
            `${this.$options.name}-selectedGroupedBy`,
            "Service"
          )
      ),
      editedItem: null,
      showAllocationItemEditor: false,
      dateFromFilter: new Date().toISOString(),
      dateToFilter: moment().add(1, "year").toISOString(false),
      serviceFilter: [],
      nameInputFilter: "",
      nameFilter: "",
      daysOfWeek: [
        { name: "Monday", value: 1 },
        { name: "Tuesday", value: 2 },
        { name: "Wednesday", value: 3 },
        { name: "Thursday", value: 4 },
        { name: "Friday", value: 5 },
        { name: "Saturday", value: 6 },
        { name: "Sunday", value: 7 },
      ],
      dayOfWeekFilter: [],
      selected: [],
      showEditAllocationsDialog: false,
    };
  },

  computed: {
    filteredHeaders() {
      const hs = [];
      for (const headerName of this.visibleColumns) {
        const h = this.headers.find((h) => h.name == headerName);
        if (h) {
          hs.push(h);
        }
      }
      //const actionsHeader = this.headers.find((h) => h.name == "actions");
      //if (actionsHeader) {
      //  hs.push(actionsHeader);
      //}
      return hs;
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    allocations() {
      return this.supplier && this.supplier.allocations
        ? [...this.getAllocations(this.supplier.allocations.edges)]
        : [];
    },
  },

  methods: {
    onEditMulti(payload) {
      this.selected = [...payload];
      this.showEditAllocationsDialog = true;
    },
    onSaveAllocations(payload) {
      const ids = this.selected.map((a) => a.id);
      const input = { ...payload };
      this.$apollo
        .mutate({
          mutation: UPDATE_ALLOCATIONS_MUTATION,
          variables: {
            ids,
            input,
          },
          refetchQueries: [
            { query: QUERY, variables: { supplierId: this.supplierId } },
          ],
        })
        .then((res) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Allocations updated.",
          });
          this.selected = [];
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
    getAllocations(edges) {
      const allocations = edges
        .map(removeGQLTypename)
        .sort(sortByDateField("date"));
      return allocations.map((a) => {
        const allPerDay = allocations.filter(
          (i) => i.date == a.date && i.uid == a.uid
        );
        const overallTaken = getTotal(allPerDay, (i) => i.taken);
        //same calculation as on rails (service.rb:560)
        const remaining = a.maxTotal - a.taken;
        const remainingOverall = a.qty - overallTaken;
        const sharedRows = allocations.filter((i) => i.uid === a.uid);
        const sharedServiceNames = [
          ...new Set(sharedRows.map((i) => i.service.name)),
        ];
        return {
          ...a,
          remaining:
            remainingOverall < remaining ? remainingOverall : remaining,
          agentGroupName: a.agentGroup ? a.agentGroup.name : "All Groups",
          isShared: sharedServiceNames.length > 1,
          sharedServices: sharedServiceNames.join(", "),
        };
      });
    },
    onDatesChange(payload) {
      this.dateFromFilter = payload.dateFrom;
      this.dateToFilter = payload.dateTo;
    },
    onNameFilterBlur(e) {
      if (this.nameInputFilter != "") {
        this.nameFilter = this.nameInputFilter;
      } else {
        this.nameFilter = "";
      }
    },
    onShowGroupedViewChange(event) {
      this.$store.dispatch("updateLocalSettings", {
        [`${this.$options.name}-showGroupedView`]: event,
      });
    },
    onSelectedGroupedByChange(payload) {
      console.log("selected grouped by: ", payload);
      this.$store.dispatch("updateLocalSettings", {
        [`${this.$options.name}-selectedGroupedBy`]: payload.name,
      });
    },
    onAllocationEdit(data) {
      this.editedItem = { ...data };
      this.showAllocationItemEditor = true;
    },
    async onAllocationItemSave(payload) {
      this.showAllocationItemEditor = false;
      try {
        const result = await this.$apollo.mutate({
          mutation: UPDATE_ALLOCATION_MUTATION,
          variables: {
            id: this.editedItem.id,
            allocationInput: {
              qty: parseInt(this.editedItem.qty),
              releaseDays: parseInt(this.editedItem.releaseDays),
              maxTotal: parseFloat(this.editedItem.maxTotal),
            },
          },
          refetchQueries: [
            {
              query: QUERY,
              variables: {
                supplierId: this.supplierId,
                filter: {
                  date_gte:
                    this.dateFromFilter != "" ? this.dateFromFilter : undefined,
                  date_lte:
                    this.dateToFilter != "" ? this.dateToFilter : undefined,
                  services: this.serviceFilter.length
                    ? this.serviceFilter
                    : undefined,
                  name_contains:
                    this.nameFilter != "" ? this.nameFilter : undefined,
                  day_of_week_eq: this.dayOfWeekFilter.length
                    ? this.dayOfWeekFilter
                    : undefined,
                },
              },
            },
          ],
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Allocation updated",
        });
        console.log(result);
      } catch (error) {
        console.log(error);
      }
    },
    async onAllocationDelete(data) {
      try {
        const result = await this.$apollo.mutate({
          mutation: DELETE_ALLOCATION_MUTATION,
          variables: {
            id: data.id,
          },
          refetchQueries: [
            { query: QUERY, variables: { supplierId: this.supplierId } },
          ],
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Allocation removed",
        });
      } catch (error) {
        console.log(error);
      }
    },
    async onAllocationsCreate(data) {
      try {
        const result = await this.$apollo.mutate({
          mutation: ADD_ALLOCATION_MUTATION,
          variables: {
            supplierId: this.supplierId,
            supplierAddAllocationsInput: data,
          },
          refetchQueries: [
            { query: QUERY, variables: { supplierId: this.supplierId } },
          ],
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Alloactions added",
        });
        console.log(result);
      } catch (error) {
        console.log(error);
      }
    },
    async onDeleteRecords(payload) {
      try {
        const resp = await this.$apollo.mutate({
          mutation: REMOVE_RECORDS,
          variables: { input: payload.map((item) => item.id) },
          refetchQueries: [
            { query: QUERY, variables: { supplierId: this.supplierId } },
          ],
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Alloactions removed",
        });
      } catch (error) {
        console.error(error);
      }
    },
  },
  apollo: {
    supplier: {
      query: QUERY,
      variables() {
        return {
          supplierId: this.supplierId,
          filter: {
            date_gte:
              this.dateFromFilter != "" ? this.dateFromFilter : undefined,
            date_lte: this.dateToFilter != "" ? this.dateToFilter : undefined,
            services: this.serviceFilter.length
              ? this.serviceFilter
              : undefined,
            name_contains: this.nameFilter != "" ? this.nameFilter : undefined,
            day_of_week_eq: this.dayOfWeekFilter.length
              ? this.dayOfWeekFilter
              : undefined,
          },
        };
      },
      fetchPolicy: "network-only",
      skip() {
        return !this.supplierId;
      },
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/suppliers/allocations/SupplierAllocations.vue